import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase.js';
import { useAuth } from '../../context/AuthContext.js';
import LoadingSpinner from '../LoadingSpinner.js';
import UserSubscriptions from '../UserSubscriptions.js';
import { FiTrash, FiPlus } from 'react-icons/fi';

// Mock data for offline development
const MOCK_USER_DATA = {
  username: 'developer_user',
  displayName: 'Development Account',
  bio: 'This is sample data for offline development.',
  profileImage: '',
  isStreamer: true,
  isPremium: true,
  user_balance: 1000,
  socialLinks: {
    twitch: 'developer_user',
    twitter: 'developer_user',
    youtube: '',
    instagram: '',
    discord: 'developer_user#1234',
    tiktok: ''
  }
};

const SOCIAL_PLATFORMS = [
  { id: 'twitch', name: 'Twitch', placeholder: 'username' },
  { id: 'twitter', name: 'Twitter', placeholder: 'username' },
  { id: 'youtube', name: 'YouTube', placeholder: 'channel name or URL' },
  { id: 'instagram', name: 'Instagram', placeholder: 'username' },
  { id: 'discord', name: 'Discord', placeholder: 'username#0000' },
  { id: 'tiktok', name: 'TikTok', placeholder: 'username' },
  { id: 'facebook', name: 'Facebook', placeholder: 'username or page URL' },
  { id: 'reddit', name: 'Reddit', placeholder: 'u/username' }
];

const ProfileSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [userData, setUserData] = useState({
    username: '',
    displayName: '',
    bio: '',
    profileImage: '',
    isStreamer: false,
    isPremium: false,
    user_balance: 0,
    socialLinks: {}
  });
  const [activeSocialLinks, setActiveSocialLinks] = useState([]);
  const [customSocialLinks, setCustomSocialLinks] = useState([]);
  const [isOffline, setIsOffline] = useState(false);
  
  // Get current user from auth context
  const { currentUser } = useAuth();

  // Monitor auth state and fetch user data
  useEffect(() => {
    const loadUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        setIsOffline(false);
        
        // Check if user is authenticated
        if (!currentUser) {
          setLoading(false);
          setError("You must be logged in to view settings");
          return;
        }
        
        console.log('Fetching user data for settings, UID:', currentUser.uid);
        
        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const data = userDocSnap.data();
            console.log('User data retrieved for settings');
            
            setUserData({
              username: data.username || '',
              displayName: data.displayName || data.username || '',
              bio: data.bio || '',
              profileImage: data.profileImage || '',
              isStreamer: !!data.isStreamer,
              isPremium: !!data.isPremium,
              user_balance: data.user_balance || 0,
              socialLinks: data.socialLinks || {}
            });
            
            // Initialize active social links from user data
            initializeSocialLinks(data.socialLinks || {});
          } else {
            console.log('No user document found for UID:', currentUser.uid);
            toast.error('User profile not found');
            setError('User profile not found in database');
          }
        } catch (error) {
          // Check if the error is due to being offline
          if (error.message.includes('offline') || 
              error.message.includes('network') ||
              error.message.includes('connection')) {
            console.log('Offline mode detected, using mock data');
            setIsOffline(true);
            
            // Use mock data when offline
            setUserData(MOCK_USER_DATA);
            // Initialize active social links from mock data
            initializeSocialLinks(MOCK_USER_DATA.socialLinks || {});
            toast.success('Using offline development data');
          } else {
            throw error; // Rethrow for other errors
          }
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        toast.error('Failed to load profile settings');
        setError(`Error: ${error.message}`);
        
        // Retry logic for network errors
        if (retryCount < 3 && (
          error.message.includes('offline') || 
          error.message.includes('connection') ||
          error.message.includes('network')
        )) {
          const nextRetry = retryCount + 1;
          toast.error(`Connection error. Retrying (${nextRetry}/3)...`);
          setTimeout(() => {
            setRetryCount(nextRetry);
          }, 2000);
        }
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [currentUser, retryCount]);

  // Function to initialize social links from user data
  const initializeSocialLinks = (links) => {
    // Set active platforms based on existing data
    const active = Object.keys(links).filter(platform => {
      // Check if it's a standard platform
      const isStandard = SOCIAL_PLATFORMS.some(p => p.id === platform);
      return isStandard;
    });
    
    // Find custom platforms (those not in our standard list)
    const custom = Object.entries(links)
      .filter(([platform]) => !SOCIAL_PLATFORMS.some(p => p.id === platform))
      .map(([platform, value]) => ({ platform, value }));
    
    setActiveSocialLinks(active);
    setCustomSocialLinks(custom);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Check if user is authenticated
      if (!currentUser) {
        toast.error('You must be logged in to update your profile');
        return;
      }

      // If offline, just simulate a successful update
      if (isOffline) {
        // Simulate delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        toast.success('Profile updated successfully (offline mode)');
        return;
      }

      const userDocRef = doc(db, 'users', currentUser.uid);
      
      // Prepare social links object for saving
      const socialLinksToSave = {};
      
      // Add standard social links
      activeSocialLinks.forEach(platformId => {
        if (userData.socialLinks[platformId]) {
          socialLinksToSave[platformId] = userData.socialLinks[platformId];
        }
      });
      
      // Add custom social links
      customSocialLinks.forEach(({ platform, value }) => {
        if (value) {
          socialLinksToSave[platform] = value;
        }
      });
      
      // Only update fields that should be editable by the user
      await updateDoc(userDocRef, {
        displayName: userData.displayName,
        bio: userData.bio,
        socialLinks: socialLinksToSave
      });
      
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(`Failed to update profile: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSocialLinkChange = (platform, value) => {
    setUserData(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [platform]: value
      }
    }));
  };

  const handleCustomSocialLinkChange = (index, field, value) => {
    const updatedLinks = [...customSocialLinks];
    updatedLinks[index][field] = value;
    setCustomSocialLinks(updatedLinks);
  };

  const addCustomSocialLink = () => {
    setCustomSocialLinks([...customSocialLinks, { platform: '', value: '' }]);
  };

  const removeCustomSocialLink = (index) => {
    const updatedLinks = [...customSocialLinks];
    updatedLinks.splice(index, 1);
    setCustomSocialLinks(updatedLinks);
  };

  const toggleSocialPlatform = (platformId) => {
    if (activeSocialLinks.includes(platformId)) {
      setActiveSocialLinks(activeSocialLinks.filter(id => id !== platformId));
    } else {
      setActiveSocialLinks([...activeSocialLinks, platformId]);
    }
  };

  const handleRetry = () => {
    setRetryCount(count => count + 1);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px]">
        <LoadingSpinner />
        <p className="mt-4 text-harmony-white">
          Loading profile settings...
          {retryCount > 0 && ` (Retry ${retryCount}/3)`}
        </p>
      </div>
    );
  }

  if (error && !isOffline) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px]">
        <div className="text-red-500 text-center mb-4">
          <p className="text-lg font-semibold">Error</p>
          <p>{error}</p>
        </div>
        {retryCount < 3 && (
          <button
            onClick={handleRetry}
            className="px-4 py-2 bg-harmony-orange text-white rounded-lg hover:bg-harmony-orange/80"
          >
            Try Again
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isOffline && (
        <div className="bg-blue-500/20 text-blue-200 p-4 rounded-lg mb-4 border border-blue-500/30">
          <p className="font-medium">⚠️ Offline Development Mode</p>
          <p className="text-sm mt-1">Using mock data for development. Changes won't be saved to Firebase.</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="bg-harmony-dark-800 p-6 rounded-lg border border-harmony-white/10">
        <h2 className="text-xl font-bold text-harmony-white mb-4">
          Profile Information
        </h2>
        
        <div className="mb-4">
          <label className="block text-harmony-white mb-2">Username</label>
          <input
            type="text"
            name="username"
            value={userData.username}
            disabled
            className="w-full px-4 py-2 bg-harmony-dark-900 text-harmony-white rounded border border-harmony-white/10 cursor-not-allowed opacity-70"
          />
          <p className="text-harmony-white/60 text-sm mt-1">Username cannot be changed</p>
        </div>
        
        <div className="mb-4">
          <label className="block text-harmony-white mb-2">Display Name</label>
          <input
            type="text"
            name="displayName"
            value={userData.displayName}
            onChange={handleChange}
            className="w-full px-4 py-2 bg-harmony-dark-900 text-harmony-white rounded border border-harmony-white/10 focus:outline-none focus:border-harmony-orange"
            maxLength={30}
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-harmony-white mb-2">Bio</label>
          <textarea
            name="bio"
            value={userData.bio}
            onChange={handleChange}
            className="w-full px-4 py-2 bg-harmony-dark-900 text-harmony-white rounded border border-harmony-white/10 focus:outline-none focus:border-harmony-orange"
            rows={4}
            maxLength={250}
          ></textarea>
          <p className="text-harmony-white/60 text-sm mt-1">
            {userData.bio.length}/250 characters
          </p>
        </div>
        
        {/* Social Links Section */}
        <div className="mt-8 mb-6">
          <h3 className="text-lg font-bold text-harmony-white mb-4">
            Social Links
          </h3>
          <p className="text-harmony-white/60 text-sm mb-4">
            Add your social media links so others can find and follow you elsewhere.
          </p>
          
          {/* Platform toggles */}
          <div className="mb-4">
            <p className="text-harmony-white mb-2">Select platforms:</p>
            <div className="flex flex-wrap gap-2">
              {SOCIAL_PLATFORMS.map(platform => (
                <button
                  key={platform.id}
                  type="button"
                  onClick={() => toggleSocialPlatform(platform.id)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    activeSocialLinks.includes(platform.id)
                      ? 'bg-harmony-orange text-white'
                      : 'bg-harmony-dark-900 text-harmony-white/60 hover:bg-harmony-white/10'
                  }`}
                >
                  {platform.name}
                </button>
              ))}
            </div>
          </div>
          
          {/* Active social links */}
          {activeSocialLinks.length > 0 && (
            <div className="space-y-3 mb-6">
              {activeSocialLinks.map(platformId => {
                const platform = SOCIAL_PLATFORMS.find(p => p.id === platformId);
                return (
                  <div key={platformId} className="flex items-center gap-3">
                    <div className="w-24 flex-shrink-0">
                      <span className="text-harmony-white">{platform.name}</span>
                    </div>
                    <input
                      type="text"
                      value={userData.socialLinks[platformId] || ''}
                      onChange={(e) => handleSocialLinkChange(platformId, e.target.value)}
                      placeholder={platform.placeholder}
                      className="flex-grow px-4 py-2 bg-harmony-dark-900 text-harmony-white rounded border border-harmony-white/10 focus:outline-none focus:border-harmony-orange"
                    />
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Custom social links */}
          {customSocialLinks.length > 0 && (
            <>
              <h4 className="text-md font-semibold text-harmony-white mt-6 mb-3">
                Custom Links
              </h4>
              <div className="space-y-3 mb-4">
                {customSocialLinks.map((link, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <input
                      type="text"
                      value={link.platform}
                      onChange={(e) => handleCustomSocialLinkChange(index, 'platform', e.target.value)}
                      placeholder="Platform name"
                      className="w-24 flex-shrink-0 px-4 py-2 bg-harmony-dark-900 text-harmony-white rounded border border-harmony-white/10 focus:outline-none focus:border-harmony-orange"
                    />
                    <input
                      type="text"
                      value={link.value}
                      onChange={(e) => handleCustomSocialLinkChange(index, 'value', e.target.value)}
                      placeholder="Username or URL"
                      className="flex-grow px-4 py-2 bg-harmony-dark-900 text-harmony-white rounded border border-harmony-white/10 focus:outline-none focus:border-harmony-orange"
                    />
                    <button
                      type="button"
                      onClick={() => removeCustomSocialLink(index)}
                      className="p-2 text-red-400 hover:text-red-300 transition-colors"
                    >
                      <FiTrash size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          
          {/* Add custom link button */}
          <button
            type="button"
            onClick={addCustomSocialLink}
            className="flex items-center gap-2 text-harmony-orange hover:text-harmony-orange/80 transition-colors mt-2"
          >
            <FiPlus size={16} />
            <span>Add Custom Link</span>
          </button>
        </div>
        
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className={`px-4 py-2 rounded-lg ${
              saving
                ? 'bg-harmony-orange/50 cursor-not-allowed'
                : 'bg-harmony-orange hover:bg-harmony-orange/80'
            } text-white transition-colors`}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
      
      {/* Subscription section */}
      <div className="mt-8">
        <h2 className="text-xl font-bold text-harmony-white mb-4">
          Your Subscriptions
        </h2>
        {isOffline ? (
          <div className="bg-harmony-dark-800 p-6 rounded-lg border border-harmony-white/10 text-center">
            <p className="text-harmony-white/60">Subscription data is not available in offline mode.</p>
          </div>
        ) : (
          <UserSubscriptions />
        )}
      </div>
    </div>
  );
};

export default ProfileSettings; 